import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ChevronDown, Filter, RotateCcw, Save, Search, Star, Trash2, X } from "lucide-react";
import type { PaperFilters, SortField, SortDirection } from "@/hooks/useFilteredPapers";
import type { PaperTag, PaperWithRole } from "@/types/paper";
import type { FilterPreset } from "@/hooks/useFilterPresets";
import type { WorkingSet } from "@/hooks/useWorkingSets";
import { TermHint } from "@/components/ui/term-hint";
import { countsAsDismissed } from "@/lib/outOfScope";

interface Props {
  filters: PaperFilters;
  onChange: (filters: PaperFilters) => void;
  papers: PaperWithRole[];
  filteredCount: number;
  sortField?: SortField;
  sortDirection?: SortDirection;
  onSortChange?: (field: SortField | undefined, direction: SortDirection) => void;
  onReset?: () => void;
  tags?: PaperTag[];
  presets?: FilterPreset[];
  onSavePreset?: (name: string, isDefault: boolean) => void;
  onLoadPreset?: (preset: FilterPreset) => void;
  onDeletePreset?: (presetId: string) => void;
  onSetDefaultPreset?: (presetId: string, isDefault: boolean) => void;
  workingSets?: WorkingSet[];
  activeWsId?: string;
  onWsChange?: (wsId: string | undefined) => void;
  onCreateWs?: (name: string) => void;
  onWsDelete?: (wsId: string) => void;
  selectedCount?: number;
  onCreateWsFromSelected?: (name: string) => void;
  totalCorpusCount?: number;
  hasTensionSort?: boolean;
  onPurgeDismissed?: () => void;
  monitorEnabled?: boolean;
  untriagedMonitorCount?: number;
  /** Papers hidden by the active filters that are safe to bulk-dismiss.
   *  Renders the "Dismiss hidden" affordance when > 0. */
  cullableCount?: number;
  onCullOthers?: () => void;
  /** Current seeds — enables the "Found via seed" filter. */
  seeds?: { id: string; title: string }[];
}

const BASE_SORT_OPTIONS: { value: SortField | ""; label: string; direction: SortDirection }[] = [
  { value: "", label: "Default", direction: "desc" },
  { value: "reading_priority", label: "Reading Priority", direction: "desc" },
  { value: "relevance_score", label: "Score", direction: "desc" },
  { value: "citations", label: "Citations", direction: "desc" },
  { value: "year", label: "Year (newest)", direction: "desc" },
  { value: "added_at", label: "Date Added", direction: "desc" },
  { value: "title", label: "Title", direction: "asc" },
];

const TENSION_SORT_OPTION: { value: SortField | ""; label: string; direction: SortDirection } = {
  value: "tension", label: "Tension-first", direction: "desc",
};

/** Reusable popover-based dropdown for filter options */
function FilterDropdown({
  value,
  placeholder,
  options,
  onSelect,
}: {
  value: string;
  placeholder: string;
  options: { value: string; label: string }[];
  onSelect: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={selected?.label ?? placeholder}
          className="flex h-8 max-w-full min-w-0 items-center gap-1.5 rounded-md border bg-background px-2 text-sm"
        >
          {/* Long labels (seed titles) truncate instead of overflowing the panel. */}
          <span className={`min-w-0 truncate ${selected?.value ? "" : "text-muted-foreground"}`}>
            {selected?.label ?? placeholder}
          </span>
          <ChevronDown className="size-3 shrink-0 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto min-w-[10rem] max-w-[18rem] p-1">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            title={opt.label}
            onClick={() => { onSelect(opt.value); setOpen(false); }}
            className={`flex w-full items-center rounded px-2 py-1.5 text-left text-sm hover:bg-accent ${
              opt.value === value ? "font-medium" : ""
            }`}
          >
            <span className="min-w-0 truncate">{opt.label}</span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function FilterBar({ filters, onChange, papers, filteredCount, sortField, sortDirection: _sortDirection, onSortChange, onReset, tags, presets, onSavePreset, onLoadPreset, onDeletePreset, onSetDefaultPreset, workingSets, activeWsId, onWsChange, onCreateWs, onWsDelete, selectedCount, onCreateWsFromSelected, totalCorpusCount, hasTensionSort, onPurgeDismissed, monitorEnabled, untriagedMonitorCount, cullableCount, onCullOthers, seeds }: Props) {
  const sortOptions = useMemo(() =>
    hasTensionSort ? [BASE_SORT_OPTIONS[0], TENSION_SORT_OPTION, ...BASE_SORT_OPTIONS.slice(1)] : BASE_SORT_OPTIONS,
  [hasTensionSort]);

  // Debounced search
  const [searchInput, setSearchInput] = useState(filters.searchText ?? "");
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    setSearchInput(filters.searchText ?? "");
  }, [filters.searchText]);

  // Active preset tracking
  const [activePresetId, setActivePresetId] = useState<string | undefined>();

  // Wrap onLoadPreset to capture the preset ID
  const handleLoadPreset = useCallback((preset: FilterPreset) => {
    setActivePresetId(preset.id);
    onLoadPreset?.(preset);
  }, [onLoadPreset]);

  // Clear active preset on manual filter change
  const handleFiltersChange = useCallback((newFilters: PaperFilters) => {
    setActivePresetId(undefined);
    onChange(newFilters);
  }, [onChange]);

  const handleSearch = useCallback(
    (value: string) => {
      setSearchInput(value);
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        handleFiltersChange({ ...filters, searchText: value || undefined });
      }, 300);
    },
    [filters, handleFiltersChange],
  );

  // Derive unique contribution types and cluster labels
  const contributionTypes = [
    ...new Set(papers.map((p) => p.contribution_type).filter(Boolean)),
  ].sort();
  const argumentationRoles = [
    ...new Set(papers.map((p) => p.argumentation_role).filter(Boolean)),
  ].sort();
  const notRelevantCount = papers.filter(
    (p) => p.user_score_override != null && p.user_score_override <= 30,
  ).length;

  const hasFilters =
    filters.searchText ||
    filters.minCitations ||
    filters.earliestYear ||
    filters.isInfluential ||
    filters.minScore ||
    filters.contributionType ||
    filters.argumentationRole ||
    (filters.overrideFilter && filters.overrideFilter !== "active") ||
    filters.readingStatus ||
    filters.hasEvidence ||
    filters.hasStashed ||
    filters.viaSeed ||
    filters.sourceFilter ||
    filters.addedWithin ||
    (filters.tagIds && filters.tagIds.length > 0) ||
    (sortField && sortField !== "relevance_score");

  const resetFilters = () => {
    setSearchInput("");
    setActivePresetId(undefined);
    if (onReset) {
      onReset();
    } else {
      onChange({});
      onSortChange?.(undefined, "desc");
    }
  };

  // Preset save dialog
  const [showSavePreset, setShowSavePreset] = useState(false);
  const [presetName, setPresetName] = useState("");
  const [presetIsDefault, setPresetIsDefault] = useState(false);

  const groupCaptionCls = "font-mono text-[8px] font-semibold uppercase tracking-[0.12em] text-muted-foreground pl-0.5";

  return (
    <div className="flex flex-wrap items-end gap-7 px-2 py-1">
      {filters.dismissedView && (
        <div className="w-full rounded-md border border-red-500/20 bg-red-500/5 px-3 py-1 text-xs text-red-400">
          Viewing dismissed papers
        </div>
      )}
      {filters.flaggedView && (
        <div className="w-full rounded-md border border-amber-500/20 bg-amber-500/5 px-3 py-1 text-xs text-amber-400">
          Viewing flagged papers
        </div>
      )}

      {/* VIEW group */}
      {((workingSets && onWsChange) || (presets && onLoadPreset) || (onSavePreset && hasFilters)) && (
        <div className="flex flex-col gap-0.5">
          <span className={groupCaptionCls}>View</span>
          <div className="flex flex-wrap items-center gap-1.5">
            {/* Working Set mode switcher */}
            {workingSets && onWsChange && (
              <WorkingSetDropdown
                workingSets={workingSets}
                activeWsId={activeWsId}
                onWsChange={onWsChange}
                onCreateWs={onCreateWs}
                onWsDelete={onWsDelete}
                selectedCount={selectedCount}
                onCreateWsFromSelected={onCreateWsFromSelected}
              />
            )}

            {/* Presets */}
            {presets && onLoadPreset && (
              <PresetDropdown
                presets={presets}
                activePresetId={activePresetId}
                onLoad={handleLoadPreset}
                onDelete={onDeletePreset}
                onSetDefault={onSetDefaultPreset}
              />
            )}

            {/* Save preset */}
            {onSavePreset && hasFilters && (
              <div className="relative">
                {showSavePreset ? (
                  <div className="flex items-center gap-1">
                    <Input
                      value={presetName}
                      onChange={(e) => setPresetName(e.target.value)}
                      placeholder="Preset name"
                      className="h-8 w-32 text-sm"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && presetName.trim()) {
                          onSavePreset(presetName.trim(), presetIsDefault);
                          setPresetName("");
                          setPresetIsDefault(false);
                          setShowSavePreset(false);
                        }
                        if (e.key === "Escape") setShowSavePreset(false);
                      }}
                    />
                    <label className="flex items-center gap-1 text-xs text-muted-foreground" title="Set as default">
                      <input
                        type="checkbox"
                        checked={presetIsDefault}
                        onChange={(e) => setPresetIsDefault(e.target.checked)}
                        className="rounded"
                      />
                      <Star className="size-3" />
                    </label>
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => {
                        if (presetName.trim()) {
                          onSavePreset(presetName.trim(), presetIsDefault);
                          setPresetName("");
                          setPresetIsDefault(false);
                          setShowSavePreset(false);
                        }
                      }}
                      disabled={!presetName.trim()}
                    >
                      Save
                    </Button>
                    <Button variant="ghost" size="xs" onClick={() => setShowSavePreset(false)}>
                      <X className="size-3" />
                    </Button>
                  </div>
                ) : (
                  <Button variant="ghost" size="xs" onClick={() => setShowSavePreset(true)}>
                    <Save className="size-3" />
                    Save
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* FILTER group */}
      <div className="flex flex-col gap-0.5">
        <div className="mb-0.5 flex items-center gap-2">
          <span className={groupCaptionCls}>Filter</span>
          <span className="h-px flex-1 bg-[rgba(88,91,112,0.5)]" />
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Filter papers..."
              value={searchInput}
              onChange={(e) => handleSearch(e.target.value)}
              className="h-8 w-48 pl-8 text-sm"
            />
          </div>

          {/* Status (override) — visible primary filter */}
          <FilterDropdown
            value={filters.overrideFilter ?? ""}
            placeholder="Active"
            options={[
              { value: "active", label: "Active" },
              { value: "", label: "All" },
              { value: "championed", label: "Marked relevant" },
              { value: "dismissed", label: `Marked not relevant${notRelevantCount > 0 ? ` (${notRelevantCount})` : ""}` },
              { value: "none", label: "No override" },
            ]}
            onSelect={(v) => handleFiltersChange({ ...filters, overrideFilter: (v || undefined) as "active" | "championed" | "dismissed" | "none" | undefined })}
          />

          {/* Score (minScore) — visible primary filter */}
          <Input
            type="number"
            placeholder="Min score"
            value={filters.minScore ?? ""}
            onChange={(e) =>
              handleFiltersChange({
                ...filters,
                minScore: e.target.value ? Number(e.target.value) : undefined,
              })
            }
            className="h-8 w-28 text-sm"
            min={0}
            max={100}
          />

          {/* Year (earliestYear) — visible primary filter */}
          <Input
            type="number"
            placeholder="From year"
            value={filters.earliestYear ?? ""}
            onChange={(e) =>
              handleFiltersChange({
                ...filters,
                earliestYear: e.target.value ? Number(e.target.value) : undefined,
              })
            }
            className="h-8 w-28 text-sm"
            min={1900}
            max={2030}
          />

          {/* Active overflow chips — render any non-default overflow filter as a removable pill */}
          {(() => {
            const chips: { key: string; label: string; clear: () => void }[] = [];
            if (filters.minCitations) {
              chips.push({
                key: "minCitations",
                label: `≥${filters.minCitations} cites`,
                clear: () => handleFiltersChange({ ...filters, minCitations: undefined }),
              });
            }
            if (filters.isInfluential) {
              chips.push({
                key: "isInfluential",
                label: "Influential",
                clear: () => handleFiltersChange({ ...filters, isInfluential: undefined }),
              });
            }
            if (filters.contributionType) {
              chips.push({
                key: "contributionType",
                label: `Type: ${filters.contributionType}`,
                clear: () => handleFiltersChange({ ...filters, contributionType: undefined }),
              });
            }
            if (filters.argumentationRole) {
              chips.push({
                key: "argumentationRole",
                label: `Role: ${filters.argumentationRole}`,
                clear: () => handleFiltersChange({ ...filters, argumentationRole: undefined }),
              });
            }
            if (filters.readingStatus) {
              const labelMap: Record<string, string> = {
                unread: "Unread",
                skimmed: "Skimmed",
                read: "Read",
              };
              chips.push({
                key: "readingStatus",
                label: labelMap[filters.readingStatus] ?? filters.readingStatus,
                clear: () => handleFiltersChange({ ...filters, readingStatus: undefined }),
              });
            }
            if (filters.hasEvidence) {
              chips.push({
                key: "hasEvidence",
                label: "Has evidence",
                clear: () => handleFiltersChange({ ...filters, hasEvidence: undefined }),
              });
            }
            if (filters.hasStashed) {
              chips.push({
                key: "hasStashed",
                label: "Stashed clips",
                clear: () => handleFiltersChange({ ...filters, hasStashed: undefined }),
              });
            }
            if (filters.sourceFilter) {
              const labelMap: Record<string, string> = {
                monitored: "Monitored",
                trawled: "Trawled",
                imported: "Imported",
              };
              chips.push({
                key: "sourceFilter",
                label: labelMap[filters.sourceFilter] ?? filters.sourceFilter,
                clear: () => handleFiltersChange({ ...filters, sourceFilter: undefined }),
              });
            }
            if (filters.viaSeed) {
              const s = seeds?.find((x) => x.id === filters.viaSeed);
              chips.push({
                key: "viaSeed",
                label: `Via seed: ${(s?.title ?? "seed").slice(0, 40)}`,
                clear: () => handleFiltersChange({ ...filters, viaSeed: undefined }),
              });
            }
            if (filters.addedWithin) {
              chips.push({
                key: "addedWithin",
                label: `Last ${filters.addedWithin}`,
                clear: () => handleFiltersChange({ ...filters, addedWithin: undefined }),
              });
            }
            if (filters.tagIds && filters.tagIds.length > 0) {
              const tag = tags?.find((t) => t.id === filters.tagIds?.[0]);
              chips.push({
                key: "tagIds",
                label: `Tag: ${tag?.name ?? "?"}`,
                clear: () => handleFiltersChange({ ...filters, tagIds: undefined }),
              });
            }

            return (
              <>
                {chips.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={c.clear}
                    className="flex h-8 items-center gap-1 rounded-full border bg-accent/40 px-2.5 text-xs text-foreground hover:bg-accent"
                    title={`Remove ${c.label}`}
                  >
                    <span>{c.label}</span>
                    <X className="size-3 text-muted-foreground" />
                  </button>
                ))}

                {/* All filters overflow */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="xs" className="h-8 gap-1.5">
                      <Filter className="size-3" />
                      All filters
                      {chips.length > 0 && (
                        <span className="ml-0.5 rounded-full bg-primary/15 px-1.5 py-0 text-[10px] font-medium leading-tight text-primary">
                          {chips.length}
                        </span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-3 space-y-3" align="start">
                    {/* Min citations */}
                    <div>
                      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Min citations
                      </label>
                      <Input
                        type="number"
                        placeholder="0"
                        value={filters.minCitations ?? ""}
                        onChange={(e) =>
                          handleFiltersChange({
                            ...filters,
                            minCitations: e.target.value ? Number(e.target.value) : undefined,
                          })
                        }
                        className="h-8 w-full text-sm"
                        min={0}
                      />
                    </div>

                    {/* Influential */}
                    <label className="flex items-center gap-2 text-sm text-foreground">
                      <input
                        type="checkbox"
                        checked={filters.isInfluential ?? false}
                        onChange={(e) =>
                          handleFiltersChange({
                            ...filters,
                            isInfluential: e.target.checked || undefined,
                          })
                        }
                        className="rounded"
                      />
                      Influential papers only
                    </label>

                    {/* Stashed evidence clips (session pending-evidence tray) */}
                    <label
                      className="flex items-center gap-2 text-sm text-foreground"
                      title="Papers with clips stashed this session that aren't attached to a claim yet"
                    >
                      <input
                        type="checkbox"
                        checked={filters.hasStashed ?? false}
                        onChange={(e) =>
                          handleFiltersChange({
                            ...filters,
                            hasStashed: e.target.checked || undefined,
                          })
                        }
                        className="rounded"
                      />
                      Has stashed clips
                    </label>

                    {/* Contribution type */}
                    {contributionTypes.length > 0 && (
                      <div>
                        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Contribution type
                        </label>
                        <FilterDropdown
                          value={filters.contributionType ?? ""}
                          placeholder="All types"
                          options={[
                            { value: "", label: "All types" },
                            ...contributionTypes.map((ct) => ({ value: ct, label: ct })),
                          ]}
                          onSelect={(v) => handleFiltersChange({ ...filters, contributionType: v || undefined })}
                        />
                      </div>
                    )}

                    {/* Argumentation role */}
                    {argumentationRoles.length > 0 && (
                      <div>
                        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Argumentation role
                        </label>
                        <FilterDropdown
                          value={filters.argumentationRole ?? ""}
                          placeholder="All roles"
                          options={[
                            { value: "", label: "All roles" },
                            ...argumentationRoles.map((ar) => ({ value: ar, label: ar })),
                          ]}
                          onSelect={(v) => handleFiltersChange({ ...filters, argumentationRole: v || undefined })}
                        />
                      </div>
                    )}

                    {/* Reading status */}
                    <div>
                      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Reading status
                      </label>
                      <FilterDropdown
                        value={filters.readingStatus ?? ""}
                        placeholder="All reading"
                        options={[
                          { value: "", label: "All reading" },
                          { value: "unread", label: "Unread" },
                          { value: "skimmed", label: "Skimmed" },
                          { value: "read", label: "Read" },
                        ]}
                        onSelect={(v) => handleFiltersChange({ ...filters, readingStatus: (v || undefined) as "unread" | "skimmed" | "read" | undefined })}
                      />
                    </div>

                    {/* Source */}
                    <div>
                      <label className="mb-1 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Source <TermHint term="trawl" />
                      </label>
                      <FilterDropdown
                        value={filters.sourceFilter ?? ""}
                        placeholder="All sources"
                        options={[
                          { value: "", label: "All sources" },
                          { value: "monitored", label: "Monitored" },
                          { value: "trawled", label: "Trawled" },
                          { value: "imported", label: "Imported" },
                        ]}
                        onSelect={(v) => handleFiltersChange({ ...filters, sourceFilter: (v || undefined) as "monitored" | "trawled" | "imported" | undefined })}
                      />
                    </div>

                    {/* Found via seed */}
                    {seeds && seeds.length > 0 && (
                      <div>
                        <label className="mb-1 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Found via seed <TermHint term="seed" />
                        </label>
                        <FilterDropdown
                          value={filters.viaSeed ?? ""}
                          placeholder="Any seed"
                          options={[
                            { value: "", label: "Any seed" },
                            ...seeds.map((s) => ({ value: s.id, label: s.title.slice(0, 60) })),
                          ]}
                          onSelect={(v) => handleFiltersChange({ ...filters, viaSeed: v || undefined })}
                        />
                      </div>
                    )}

                    {/* Recency */}
                    <div>
                      <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        Added within
                      </label>
                      <FilterDropdown
                        value={filters.addedWithin ?? ""}
                        placeholder="All time"
                        options={[
                          { value: "", label: "All time" },
                          { value: "24h", label: "Last 24h" },
                          { value: "7d", label: "Last 7d" },
                          { value: "30d", label: "Last 30d" },
                        ]}
                        onSelect={(v) => handleFiltersChange({ ...filters, addedWithin: (v || undefined) as "24h" | "7d" | "30d" | undefined })}
                      />
                    </div>

                    {/* Tag */}
                    {tags && tags.length > 0 && (
                      <div>
                        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          Tag
                        </label>
                        <TagFilterDropdown
                          tags={tags}
                          selectedTagId={filters.tagIds?.length === 1 ? filters.tagIds[0] : undefined}
                          onSelect={(tagId) =>
                            handleFiltersChange({ ...filters, tagIds: tagId ? [tagId] : undefined })
                          }
                        />
                      </div>
                    )}
                  </PopoverContent>
                </Popover>
              </>
            );
          })()}
        </div>
      </div>

      {/* SORT group */}
      {onSortChange && (
        <div className="flex flex-col gap-0.5">
          <span className={groupCaptionCls}>Sort</span>
          <div className="flex flex-wrap items-center gap-1.5">
            <FilterDropdown
              value={sortField ?? ""}
              placeholder="Default"
              options={sortOptions.map((opt) => ({ value: opt.value, label: opt.label }))}
              onSelect={(v) => {
                if (!v) {
                  onSortChange(undefined, "desc");
                } else {
                  const opt = sortOptions.find((o) => o.value === v);
                  onSortChange(v as SortField, opt?.direction ?? "desc");
                }
              }}
            />
          </div>
        </div>
      )}

      {/* Reset */}
      {hasFilters && (
        <Button variant="ghost" size="xs" onClick={resetFilters}>
          <RotateCcw className="size-3" />
          Reset
        </Button>
      )}

      {/* Monitor inbox toggle */}
      {(() => {
        const isInboxView = !!filters.monitorInboxView;
        const count = untriagedMonitorCount ?? 0;
        if (!monitorEnabled && !isInboxView) return null;
        if (count === 0 && !isInboxView) return null;
        return (
          <button
            onClick={() => {
              if (isInboxView) {
                onChange({ ...filters, monitorInboxView: undefined });
              } else {
                onChange({
                  searchText: filters.searchText,
                  monitorInboxView: true,
                });
              }
            }}
            className={`rounded-full border px-2 py-0.5 text-xs transition-colors ${
              isInboxView
                ? "border-sky-500/50 bg-sky-500/10 text-sky-400 font-medium"
                : "border-sky-500/30 text-sky-600 hover:bg-sky-500/10"
            }`}
          >
            {isInboxView ? "← Back to active papers" : `${count} to triage`}
          </button>
        );
      })()}

      {/* Flagged view toggle */}
      {(() => {
        const flaggedCount = papers.filter((p) => p.flagged_for_inbox).length;
        const isFlaggedView = !!filters.flaggedView;
        // Always show when in flagged view (so user can exit even if count drops to 0)
        if (flaggedCount === 0 && !isFlaggedView) return null;
        return (
          <button
            onClick={() => {
              if (isFlaggedView) {
                onChange({ ...filters, flaggedView: undefined, showFlagged: undefined });
              } else {
                onChange({
                  searchText: filters.searchText,
                  flaggedView: true,
                  showFlagged: true,
                });
              }
            }}
            className={`rounded-full border px-2 py-0.5 text-xs transition-colors ${
              isFlaggedView
                ? "border-amber-500/50 bg-amber-500/10 text-amber-400 font-medium"
                : "border-amber-500/30 text-amber-600 hover:bg-amber-500/10"
            }`}
          >
            {isFlaggedView ? "← Back to active papers" : `${flaggedCount} flagged`}
          </button>
        );
      })()}

      {/* Dismissed view toggle + purge */}
      {(() => {
        const excludedCount = papers.filter(countsAsDismissed).length;
        const isDismissedView = !!filters.dismissedView;
        // Always show when in dismissed view (so user can exit after purge)
        if (excludedCount === 0 && !isDismissedView) return null;

        return (
          <>
            <button
              onClick={() => {
                if (isDismissedView) {
                  // Exit dismissed view — clear dismissedView flag
                  onChange({ ...filters, dismissedView: undefined, showDismissed: undefined });
                } else {
                  // Enter dismissed view — set dismissedView, reset conflicting filters
                  onChange({
                    searchText: filters.searchText,
                    dismissedView: true,
                    showDismissed: true,
                  });
                }
              }}
              className={`rounded-full border px-2 py-0.5 text-xs transition-colors ${
                isDismissedView
                  ? "border-red-500/50 bg-red-500/10 text-red-400 font-medium"
                  : "text-muted-foreground hover:bg-muted"
              }`}
            >
              {isDismissedView ? "← Back to active papers" : `${excludedCount} dismissed`}
            </button>
            {isDismissedView && onPurgeDismissed && (
              <button
                onClick={() => {
                  if (window.confirm(`Permanently delete ${excludedCount} dismissed paper${excludedCount !== 1 ? "s" : ""}? This cannot be undone.`)) {
                    onPurgeDismissed();
                  }
                }}
                className="rounded-full border border-red-500/30 px-2 py-0.5 text-xs text-red-400 hover:bg-red-500/10 transition-colors"
                title="Permanently remove all dismissed papers"
              >
                purge
              </button>
            )}
          </>
        );
      })()}

      {/* Summary */}
      <span className="ml-auto text-xs text-muted-foreground">
        {totalCorpusCount != null && activeWsId
          ? `${totalCorpusCount} in corpus · ${papers.length} in set · Showing ${filteredCount}`
          : `${totalCorpusCount ?? papers.length} in corpus · Showing ${filteredCount}`}
      </span>

      {/* Filter-complement cull: dismiss what the filters are hiding.
          cullableCount > 0 implies filters are hiding something — with no
          filters the complement is empty by construction. */}
      {onCullOthers && (cullableCount ?? 0) > 0 && (
        <button
          onClick={onCullOthers}
          className="rounded-full border border-red-500/30 px-2 py-0.5 text-xs text-red-400 hover:bg-red-500/10 transition-colors"
          title="Dismiss every paper hidden by the current filters"
        >
          Dismiss hidden ({cullableCount})
        </button>
      )}
    </div>
  );
}

function TagFilterDropdown({
  tags,
  selectedTagId,
  onSelect,
}: {
  tags: PaperTag[];
  selectedTagId?: string;
  onSelect: (tagId: string | undefined) => void;
}) {
  const [open, setOpen] = useState(false);
  const selectedTag = selectedTagId ? tags.find((t) => t.id === selectedTagId) : undefined;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex h-8 items-center gap-1.5 rounded-md border bg-background px-2 text-sm"
        >
          {selectedTag ? (
            <>
              <span
                className="inline-block size-2.5 rounded-full"
                style={{ backgroundColor: selectedTag.color }}
              />
              <span style={{ color: selectedTag.color }}>{selectedTag.name}</span>
            </>
          ) : (
            <span className="text-muted-foreground">All tags</span>
          )}
          <ChevronDown className="size-3 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto min-w-[10rem] p-1">
        <button
          type="button"
          onClick={() => { onSelect(undefined); setOpen(false); }}
          className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent ${!selectedTagId ? "font-medium" : ""}`}
        >
          All tags
        </button>
        {tags.map((tag) => (
          <button
            key={tag.id}
            type="button"
            onClick={() => { onSelect(tag.id); setOpen(false); }}
            className={`flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-accent ${selectedTagId === tag.id ? "font-medium" : ""}`}
          >
            <span
              className="inline-block size-2.5 rounded-full"
              style={{ backgroundColor: tag.color }}
            />
            <span style={{ color: tag.color }}>{tag.name}</span>
            <span className="ml-auto text-xs text-muted-foreground">
              {tag.paper_count}
            </span>
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

function WorkingSetDropdown({
  workingSets,
  activeWsId,
  onWsChange,
  onCreateWs,
  onWsDelete,
  selectedCount,
  onCreateWsFromSelected,
}: {
  workingSets: WorkingSet[];
  activeWsId?: string;
  onWsChange: (wsId: string | undefined) => void;
  onCreateWs?: (name: string) => void;
  onWsDelete?: (wsId: string) => void;
  selectedCount?: number;
  onCreateWsFromSelected?: (name: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState<"new" | "selected" | false>(false);
  const [newName, setNewName] = useState("");
  const activeWs = activeWsId ? workingSets.find((ws) => ws.id === activeWsId) : undefined;

  const handleCreate = () => {
    const trimmed = newName.trim();
    if (!trimmed) return;
    if (creating === "selected" && onCreateWsFromSelected) {
      onCreateWsFromSelected(trimmed);
    } else if (onCreateWs) {
      onCreateWs(trimmed);
    }
    setNewName("");
    setCreating(false);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setCreating(false); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-guide-target="workingSetsButton"
          className={`flex h-8 items-center gap-1.5 rounded-md border bg-background px-2 text-sm ${
            activeWs ? "border-[#89b4fa] text-[#89b4fa]" : ""
          }`}
        >
          <span className={activeWs ? "" : "text-muted-foreground"}>
            {activeWs ? activeWs.name : "Full Corpus"}
          </span>
          <ChevronDown className="size-3 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-52 p-1">
        <button
          type="button"
          onClick={() => { onWsChange(undefined); setOpen(false); }}
          className={`flex w-full items-center rounded px-2 py-1.5 text-sm hover:bg-accent ${!activeWsId ? "font-medium" : ""}`}
        >
          Full Corpus
        </button>
        {workingSets.map((ws) => (
          <div
            key={ws.id}
            className="group flex items-center gap-1 rounded hover:bg-accent"
          >
            <button
              type="button"
              onClick={() => { onWsChange(ws.id); setOpen(false); }}
              className={`flex flex-1 items-center justify-between px-2 py-1.5 text-sm ${activeWsId === ws.id ? "font-medium" : ""}`}
            >
              <span>{ws.name}</span>
              <span className="text-xs text-muted-foreground">{ws.paper_count}</span>
            </button>
            {onWsDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onWsDelete(ws.id);
                }}
                className="rounded p-0.5 mr-1 opacity-0 group-hover:opacity-100 hover:bg-muted"
                title="Delete working set"
              >
                <Trash2 className="size-3 text-muted-foreground" />
              </button>
            )}
          </div>
        ))}
        <div className="mt-1 border-t pt-1">
          {creating ? (
            <div className="flex gap-1 px-1">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleCreate()}
                placeholder="Set name..."
                className="flex-1 rounded border bg-background px-2 py-1 text-sm"
                autoFocus
              />
              <Button size="xs" onClick={handleCreate} disabled={!newName.trim()}>
                Save
              </Button>
            </div>
          ) : (
            <>
              {(selectedCount ?? 0) > 0 && onCreateWsFromSelected && (
                <button
                  type="button"
                  onClick={() => setCreating("selected")}
                  className="flex w-full items-center rounded px-2 py-1.5 text-sm font-medium hover:bg-accent"
                  style={{ color: "#89b4fa" }}
                >
                  + Create from Selected ({selectedCount})
                </button>
              )}
              <button
                type="button"
                onClick={() => setCreating("new")}
                className="flex w-full items-center rounded px-2 py-1.5 text-sm text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                + New Working Set
              </button>
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function PresetDropdown({
  presets,
  activePresetId,
  onLoad,
  onDelete,
  onSetDefault,
}: {
  presets: FilterPreset[];
  activePresetId?: string;
  onLoad: (preset: FilterPreset) => void;
  onDelete?: (presetId: string) => void;
  onSetDefault?: (presetId: string, isDefault: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const activePreset = activePresetId ? presets.find((p) => p.id === activePresetId) : undefined;

  if (presets.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`flex h-8 items-center gap-1.5 rounded-md border bg-background px-2 text-sm ${
            activePreset ? "border-[#89b4fa] text-[#89b4fa]" : ""
          }`}
        >
          {activePreset ? (
            <>
              <Star className="size-3 fill-yellow-400 text-yellow-400" />
              <span>{activePreset.name}</span>
            </>
          ) : (
            <span className="text-muted-foreground">Presets</span>
          )}
          <ChevronDown className="size-3 text-muted-foreground" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto min-w-[12rem] p-1">
        {presets.map((preset) => (
          <div
            key={preset.id}
            className="group flex items-center gap-1 rounded hover:bg-accent"
          >
            <button
              type="button"
              onClick={() => { onLoad(preset); setOpen(false); }}
              className="flex flex-1 items-center gap-2 px-2 py-1.5 text-sm"
            >
              {preset.is_default && (
                <Star className="size-3 fill-yellow-400 text-yellow-400" />
              )}
              <span>{preset.name}</span>
            </button>
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100">
              {onSetDefault && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSetDefault(preset.id, !preset.is_default);
                  }}
                  className="rounded p-0.5 hover:bg-muted"
                  title={preset.is_default ? "Remove default" : "Set as default"}
                >
                  <Star className={`size-3 ${preset.is_default ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground"}`} />
                </button>
              )}
              {onDelete && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(preset.id);
                  }}
                  className="rounded p-0.5 hover:bg-muted"
                  title="Delete preset"
                >
                  <Trash2 className="size-3 text-muted-foreground" />
                </button>
              )}
            </div>
          </div>
        ))}
      </PopoverContent>
    </Popover>
  );
}
